import { BoundSelectCardGroupField, Css } from "@homebound/beam";
import type { FieldState } from "@homebound/form-state";
import { ConflictIcon } from "src/routes/projects/change-events/guided/workflows/components/ConflictIcon";
import type { ExteriorOptionCard_CheckoutConfigOptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/components/ExteriorOptionCard.generated";
import {
  conflictMessage,
  isExteriorGroup,
  type OptionTags,
} from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import {
  OptionPrice,
  OptionPriceMode,
  optionAmountInCents,
} from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { fail } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

export type ExteriorOptionCardProps = {
  group: ExteriorOptionCard_CheckoutConfigOptionGroupFragment;
  /** The form field that stores the picked `ReadyPlanOption` id. Picking one is required. */
  field: FieldState<string | null | undefined>;
  priceMode?: OptionPriceMode;
  /** Tags shown above the matching cards' labels. */
  optionTags?: OptionTags;
};

/**
 * Renders an elevation or exterior scheme group as a row of image cards to pick one from, each with its price and a
 * conflict warning when picking it would drop other selected options. Options the server rules out (e.g. schemes the
 * selected elevation doesn't support) aren't shown at all, like checkout.
 */
export function ExteriorOptionCard(props: ExteriorOptionCardProps) {
  const { group, field, priceMode = OptionPriceMode.Price, optionTags } = props;
  if (!isExteriorGroup(group)) {
    fail(`Option group ${group.id} is not an exterior group`);
  }
  return (
    <BoundSelectCardGroupField
      field={field}
      labelStyle="hidden"
      layout="horizontal"
      options={group.options
        .filter((o) => o.canBeSelected.allowed)
        .map((o) => ({
          value: o.option.id,
          label: o.option.name,
          tag: optionTags?.[o.option.id],
          image: o.rendering?.previewUrl ?? o.option.optionImage?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL,
          // The card's label is plain text, so the warning sits on its own line right under it
          description: (
            <span css={Css.df.fdc.aifs.gap1.$}>
              <ConflictIcon message={conflictMessage(o.option.name, o.wouldDeselect)} />
              <OptionPrice
                priceInCents={optionAmountInCents(o, priceMode)}
                displayDirection={priceMode === OptionPriceMode.Price}
              />
            </span>
          ),
        }))}
    />
  );
}
